// code-guardian-ignore-duplicates: shared presentational scaffolding (table/report layout from the design system)
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ChevronDown, ChevronRight, Clock, Download, Info, Package,
  RefreshCw, Settings as SettingsIcon, Store as StoreIcon, Users,
} from 'lucide-react';
import ReportPageScaffold from '../components/ReportPageScaffold';
import { Skeleton } from '../components/Skeleton';
import { SortTh, compareSortText, useTableSort } from '../components/dashboard/useTableSort';
import {
  productMasterAPI, storesAPI,
  type StockStoreStaff, type StockSummaryRow,
} from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { balancePhrases, DEFAULT_BALANCE_LABEL } from '../utils/balanceLabel';
import { invalidateBalanceLabel } from '../hooks/useBalanceLabel';
import { buildCsv } from '../utils/csv';
import { downloadBlob } from '../utils/download';

/**
 * Reports → Running Stock.
 *
 * BUILT TO ANSWER THESE WITHOUT ANY DIGGING:
 *   1. Where are we out?             → the "Out of stock" tile; one click shows only those stores
 *   2. Which shops aren't reporting? → "Stores reporting" and "Not updated in 7+ days"
 *   3. What is at a shop?            → each store row names who is deployed there and who last
 *                                      recorded a figure; opened, it lists each product with what
 *                                      is available and what was sold, the sizes with stock as chips
 *   4. How much of X do we have?     → Product view: the same lines, added up over every store
 *
 * The owner asked for exactly two figures per product (14 Oct 2026): available and
 * sold. The grid of sizes, the visit log and the "never counted" figure went; the
 * full detail (last count, added, removed, sources) is still in the download.
 *
 * Everything else — which task recorded a figure, when the last count was — is
 * still here, but demoted to a tooltip or a muted second line. The first version
 * of this page showed every detail at the same volume, so the one number that
 * mattered had to be hunted for.
 *
 * WHY A REPORT AND NOT A TAB ON PRODUCTS: Products is maintenance, opened when the
 * catalogue changes; this is operational and read daily. On-hand is not in any
 * single task — it is the last count, plus every receipt and minus every sale
 * since, across tasks that never reference each other.
 */

interface StoreOption { id: string; name: string }

/** Same figures, two groupings: "which shop is short" vs "how much of X is there". */
type View = 'store' | 'product';

interface StoreGroup {
  id: string;
  name: string;
  /** Ordered gaps-first, then A→Z. */
  rows: StockSummaryRow[];
  /** Sizes counted and now empty. */
  gaps: number;
  onHand: number;
  /** Sold since the last count, over all its products. */
  sold: number;
  lastMovementAt: string | null;
  lastBy: string | null;
  stale: boolean;
  staff: StockStoreStaff[];
}

/** Which stores the tiles narrow the list to. */
type Filter = 'all' | 'out' | 'stale';

const STALE_DAYS = 7;

const later = (a: string | null, b: string | null): string | null => {
  if (!a) return b;
  if (!b) return a;
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b;
};

/** Short date for the screen — the year only when it is not this one. */
const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const opts: Intl.DateTimeFormatOptions = d.getFullYear() === new Date().getFullYear()
    ? { day: '2-digit', month: 'short' }
    : { day: '2-digit', month: 'short', year: 'numeric' };
  return d.toLocaleDateString('en-IN', opts);
};

/** Unambiguous date for the CSV, which outlives the year it was exported in. */
const csvDate = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const EFFECT_WORD: Record<string, string> = {
  set: 'Counted', increase: 'Added', decrease: 'Sold', remove: 'Removed',
};

/** Which task question produced each figure — the audit trail, for a tooltip or the CSV. */
const sourcesText = (r: StockSummaryRow, sep: string): string =>
  r.sources
    .map((s) => `${EFFECT_WORD[s.effect] ?? s.effect} by ${s.taskTitle} · ${s.questionText}${s.count > 1 ? ` (×${s.count})` : ''}`)
    .join(sep);

/**
 * Counted, and now at or below zero. Only a counted product can be OUT: one that
 * nobody has counted reads zero because nothing is known about it, not because
 * the shelf is empty.
 */
const isGap = (r: StockSummaryRow): boolean => r.opening !== null && r.available <= 0;

/** Never counted and nothing on hand. Below zero is real: sales against an uncounted shelf. */
const isUncounted = (r: StockSummaryRow): boolean => r.opening === null && r.available <= 0;

const statusRank = (r: StockSummaryRow): number => (isGap(r) ? 0 : isUncounted(r) ? 1 : 2);

const gapsFirst = (a: StockSummaryRow, b: StockSummaryRow): number =>
  statusRank(a) - statusRank(b) || compareSortText(a.label, b.label);

const isStale = (iso: string | null): boolean => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && Date.now() - t > STALE_DAYS * 24 * 60 * 60 * 1000;
};

/** A person by name, or by code when no name is on record. */
const who = (name: string | null | undefined, code: string | null | undefined): string | null =>
  (name && name.trim()) || (code && code.trim()) || null;


/**
 * The stock list a person reads: one line per product, the sizes that have stock as
 * small chips, and two figures, available and sold.
 *
 * A catalogue that varies by size (Oura: finish × ring size) used to be drawn as a
 * grid of sixty-six cells per store. Versions with one characteristic are now lines of
 * their product; with two, lines of "product · first characteristic", the second as
 * the chips. Anything else is a line of its own.
 */
interface ProductLine {
  key: string;
  name: string;
  /** Added up over the line's sizes (and, in Product view, over every store). */
  available: number;
  /** Sold since the last count, added up the same way. */
  sold: number;
  /** At least one of its figures rests on a count. */
  counted: boolean;
  /** Sizes counted and now at or below zero (store × size in Product view). */
  gaps: number;
  /** Stores with a figure for it. */
  stores: number;
  /** The sizes whose figure is not zero, in size order. */
  sizes: { label: string; available: number }[];
}

const lineOf = (r: StockSummaryRow): { key: string; name: string; size: string | null; sizeName: string | null } => {
  const attrs = r.attributes ?? [];
  if (r.isVersion && r.productName && attrs.length === 1) {
    return { key: `1|${r.productName}`, name: r.productName, size: attrs[0].value, sizeName: attrs[0].name };
  }
  if (r.isVersion && r.productName && attrs.length === 2) {
    return {
      key: `2|${r.productName}|${attrs[0].value}`,
      name: `${r.productName} · ${attrs[0].value}`,
      size: attrs[1].value,
      sizeName: attrs[1].name,
    };
  }
  return { key: `0|${r.key}`, name: r.label, size: null, sizeName: null };
};

type LineDraft = Omit<ProductLine, 'sizes' | 'stores'> & { sizeMap: Map<string, number>; storeIds: Set<string> };

const productLines = (rows: StockSummaryRow[]): ProductLine[] => {
  const byKey = new Map<string, LineDraft>();
  rows.forEach((r) => {
    const { key, name, size } = lineOf(r);
    const line: LineDraft = byKey.get(key) ?? {
      key, name, available: 0, sold: 0, counted: false, gaps: 0, sizeMap: new Map(), storeIds: new Set(),
    };
    line.available += r.available;
    line.sold += r.reduced;
    if (r.opening !== null) line.counted = true;
    if (isGap(r)) line.gaps += 1;
    line.storeIds.add(r.storeId);
    if (size !== null) line.sizeMap.set(size, (line.sizeMap.get(size) ?? 0) + r.available);
    byKey.set(key, line);
  });
  return Array.from(byKey.values())
    .map(({ sizeMap, storeIds, ...line }) => ({
      ...line,
      stores: storeIds.size,
      sizes: Array.from(sizeMap.entries())
        .filter(([, n]) => n !== 0)
        .sort(([a], [b]) => compareSortText(a, b))
        .map(([label, available]) => ({ label, available })),
    }))
    .sort((a, b) => compareSortText(a.name, b.name));
};

/** "Available by size" when every chip is a size, "Available by version" otherwise. */
const sizesHeading = (rows: StockSummaryRow[]): string => {
  const names = new Set(
    rows.map((r) => lineOf(r).sizeName).filter((n): n is string => !!n).map((n) => n.toLowerCase()),
  );
  return names.size === 1 ? `Available by ${Array.from(names)[0]}` : 'Available by version';
};

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/** Summary tile, in the same markup as the other report pages. */
function Tile({
  icon: Icon, label, value, hint, tone = 'text-gray-900', onClick, active = false,
}: {
  icon: typeof StoreIcon;
  label: string;
  value: string | number;
  hint: string;
  tone?: string;
  onClick?: () => void;
  active?: boolean;
}) {
  const body = (
    <>
      <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${tone}`}>{value}</div>
      <div className="mt-0.5 text-xs text-gray-400">{hint}</div>
    </>
  );
  if (!onClick) return <div className="rounded-lg border border-gray-200 p-4">{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border p-4 text-left transition-colors ${
        active
          ? 'border-primary-400 bg-primary-50/40 ring-1 ring-primary-200'
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      {body}
    </button>
  );
}

// The two figures' explanations, on hover, the same in both views.
const AVAILABLE_HINT = 'The last count, plus what was added, minus what was sold or removed since that count';
const SOLD_HINT = 'Sold since the last count. Transfers and returns are not sales.';
const BELOW_ZERO = 'More sold or removed than was counted: count it again.';

/** The sizes that have a figure, as small chips: the size, then how many. */
function SizeChips({ sizes }: { sizes: ProductLine['sizes'] }) {
  if (sizes.length === 0) return <span className="text-gray-300">—</span>;
  return (
    <span className="flex flex-wrap gap-1.5">
      {sizes.map((z) => (
        <span
          key={z.label}
          title={z.available < 0 ? `${z.label}: ${z.available}. ${BELOW_ZERO}` : `${z.label}: ${z.available} available`}
          // Two halves, so "size 8, two of them" never reads as "82": the size on a
          // tint, the count on white.
          className={`inline-flex overflow-hidden rounded-md border text-xs tabular-nums ${
            z.available < 0 ? 'border-red-200 text-red-700' : 'border-gray-200 text-gray-900'
          }`}
        >
          <span className={`px-1.5 py-0.5 ${z.available < 0 ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-500'}`}>{z.label}</span>
          <span className="bg-white px-1.5 py-0.5 font-semibold">{z.available}</span>
        </span>
      ))}
    </span>
  );
}

/** A line's available figure: red at or below zero, a dash when nothing was ever counted. */
function AvailableFigure({ line }: { line: ProductLine }) {
  if (!line.counted && line.available === 0) {
    return <span className="font-normal text-gray-300" title="Not counted yet">—</span>;
  }
  if (line.available < 0) return <span className="text-red-600" title={BELOW_ZERO}>{line.available}</span>;
  if (line.available === 0) return <span className="text-red-600" title="Counted, and none left">0</span>;
  return <>{line.available}</>;
}

/** One store, opened: each product with what is available and what was sold. */
function StorePanel({ group }: { group: StoreGroup }) {
  const lines = useMemo(() => productLines(group.rows), [group.rows]);
  const heading = useMemo(() => sizesHeading(group.rows), [group.rows]);
  return (
    <div className="overflow-x-auto border-t border-gray-100">
      <table className="w-full min-w-[560px] table-fixed text-sm">
        <colgroup>
          <col className="w-56" />
          <col />
          <col className="w-28" />
          <col className="w-24" />
        </colgroup>
        <thead className="bg-gray-50/70">
          <tr className="text-left text-xs text-gray-500">
            <th className="py-2 pl-9 pr-3 font-medium">Product</th>
            <th className="px-3 py-2 font-medium">{heading}</th>
            <th className="px-3 py-2 text-right font-medium" title={AVAILABLE_HINT}>Available</th>
            <th className="py-2 pl-3 pr-4 text-right font-medium" title={SOLD_HINT}>Sold</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {lines.map((line) => (
            <tr key={line.key} className="hover:bg-gray-50/60">
              <td className="py-2.5 pl-9 pr-3 font-medium text-gray-900">{line.name}</td>
              <td className="px-3 py-2"><SizeChips sizes={line.sizes} /></td>
              <td className="px-3 py-2.5 text-right text-base font-semibold tabular-nums text-gray-900">
                <AvailableFigure line={line} />
              </td>
              <td className="py-2.5 pl-3 pr-4 text-right tabular-nums text-gray-700">
                {line.sold || <span className="text-gray-300">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function RunningStockReportPage() {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);

  const [view, setView] = useState<View>('store');
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [storeId, setStoreId] = useState('');
  const [rows, setRows] = useState<StockSummaryRow[]>([]);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [staff, setStaff] = useState<StockStoreStaff[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const toggleFilter = (next: Filter) => setFilter((cur) => (cur === next ? 'all' : next));
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Project settings. Set once in a project's life, so they live behind a button
  // rather than above the numbers every time the report opens.
  const [showSettings, setShowSettings] = useState(false);
  const [savingFlag, setSavingFlag] = useState(false);
  // `label` is the saved value, `labelDraft` what the admin is typing — kept apart
  // so the page's wording does not rewrite itself mid-word.
  const [label, setLabel] = useState('');
  const [labelDraft, setLabelDraft] = useState('');
  const [savingLabel, setSavingLabel] = useState(false);
  const phrases = useMemo(() => balancePhrases(label), [label]);

  useEffect(() => {
    if (!selectedProjectId) return;
    storesAPI
      .getAll(selectedProjectId)
      .then((res) => {
        const list = (res.data?.data ?? res.data ?? []) as any[];
        setStores(
          list
            .map((s) => ({ id: String(s.id), name: s.store_name || s.name || s.store_code || String(s.id) }))
            .sort((a, b) => compareSortText(a.name, b.name)),
        );
      })
      .catch(() => setStores([]));
  }, [selectedProjectId]);

  const load = useCallback(() => {
    if (!selectedProjectId) { setRows([]); return; }
    setLoading(true);
    setError(null);
    productMasterAPI
      .getStockLedger(selectedProjectId, storeId || undefined)
      .then((res) => {
        const data = res.data?.data ?? {};
        setRows((data.rows ?? []) as StockSummaryRow[]);
        setStaff((data.staff ?? []) as StockStoreStaff[]);
        setEnabled(data.enabled === true);
        const saved = String(data.label ?? '') || DEFAULT_BALANCE_LABEL;
        setLabel(saved);
        setLabelDraft(saved);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || phrases.couldNotLoad);
        setRows([]);
        setStaff([]);
      })
      .finally(() => setLoading(false));
    // phrases derives from `label`, which this very call sets — depending on it
    // would refetch on every rename.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, storeId]);

  useEffect(() => { load(); }, [load]);

  const toggleTracking = async (next: boolean) => {
    if (!selectedProjectId) return;
    setSavingFlag(true);
    try {
      await productMasterAPI.setStockTrackingEnabled(selectedProjectId, next);
      setEnabled(next);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not change the setting.');
    } finally {
      setSavingFlag(false);
    }
  };

  const saveLabel = async () => {
    if (!selectedProjectId) return;
    const next = labelDraft.trim();
    if (next === label.trim()) return;
    setSavingLabel(true);
    try {
      const res = await productMasterAPI.setBalanceLabel(selectedProjectId, next);
      // Trust the server's answer, not the draft: it trims, caps and defaults.
      const saved = String(res.data?.data?.label ?? '') || DEFAULT_BALANCE_LABEL;
      setLabel(saved);
      setLabelDraft(saved);
      invalidateBalanceLabel(selectedProjectId);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not save that name.');
      setLabelDraft(label);
    } finally {
      setSavingLabel(false);
    }
  };

  const searching = search.trim() !== '';
  // Anything that makes an empty group meaningless and wants the matches open.

  // In Product view the search matches the product only. Matching a store name
  // there would quietly re-total each product over just that shop, and the
  // figures would stop meaning "the whole network".
  const searched = useMemo(() => {
    const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return rows;
    return rows.filter((r) => {
      const hay = (view === 'store' ? `${r.label} ${r.storeName ?? ''}` : r.label).toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  }, [rows, search, view]);

  // Headline figures for the whole scope. Deliberately NOT affected by search or
  // the out-of-stock filter — they are what those filters are measured against.
  const summary = useMemo(() => {
    const reporting = new Set(rows.map((r) => r.storeId));
    const totalStores = storeId ? 1 : Math.max(stores.length, reporting.size);
    const gapRows = rows.filter(isGap);
    const lastByStore = new Map<string, string | null>();
    rows.forEach((r) => lastByStore.set(r.storeId, later(lastByStore.get(r.storeId) ?? null, r.lastMovementAt)));
    return {
      staleStores: Array.from(lastByStore.values()).filter(isStale).length,
      reporting: reporting.size,
      totalStores,
      silent: Math.max(totalStores - reporting.size, 0),
      products: productLines(rows).length,
      versions: new Set(rows.map((r) => r.key)).size,
      gaps: gapRows.length,
      gapStores: new Set(gapRows.map((r) => r.storeId)).size,
      lastAt: rows.reduce<string | null>((acc, r) => later(acc, r.lastMovementAt), null),
    };
  }, [rows, stores.length, storeId]);

  const storeGroups = useMemo<StoreGroup[]>(() => {
    const byStore = new Map<string, StockSummaryRow[]>();
    searched.forEach((r) => {
      const list = byStore.get(r.storeId) ?? [];
      list.push(r);
      byStore.set(r.storeId, list);
    });

    // From the project's STORE LIST, not from the movements: a shop with nothing
    // recorded is itself a finding and has to be visible without asking.
    const base = storeId ? stores.filter((s) => s.id === storeId) : stores;
    const named = base.map((s) => ({ id: s.id, name: s.name }));
    // A shop with figures but gone from the list (deleted, or moved projects).
    // Dropping it would silently lose recorded movements.
    byStore.forEach((rs, id) => {
      if (!named.some((s) => s.id === id)) named.push({ id, name: rs[0].storeName });
    });

    const groups: StoreGroup[] = named.map((s) => {
      const rs = (byStore.get(s.id) ?? []).slice().sort(gapsFirst);
      const lastMovementAt = rs.reduce<string | null>((acc, r) => later(acc, r.lastMovementAt), null);
      const lastRow = rs.find((r) => r.lastMovementAt === lastMovementAt);
      return {
        id: s.id,
        name: s.name,
        rows: rs,
        gaps: rs.filter(isGap).length,
        onHand: rs.reduce((sum, r) => sum + r.available, 0),
        sold: rs.reduce((sum, r) => sum + r.reduced, 0),
        lastMovementAt,
        lastBy: lastRow ? who(lastRow.lastByName, lastRow.lastByCode) : null,
        stale: isStale(lastMovementAt),
        staff: staff.filter((p) => p.storeId === s.id),
      };
    });

    // The tiles pick STORES. An opened store always shows everything it has —
    // its grid already marks what is out and what was never counted.
    const matches = (g: StoreGroup): boolean => {
      if (filter === 'out') return g.gaps > 0;
      if (filter === 'stale') return g.stale;
      return searching ? g.rows.length > 0 : true;
    };

    // ⭐ PROBLEM FIRST: shops with gaps (most first), then shops that report and
    // are fine, then shops that have recorded nothing — A→Z inside each band.
    // Pure A→Z looked tidy and buried the three shops that needed a call under
    // the twenty-seven that did not.
    const rank = (g: StoreGroup): number => (g.gaps > 0 ? 0 : g.rows.length > 0 ? 1 : 2);
    return groups.filter(matches).sort(
      (a, b) => rank(a) - rank(b) || b.gaps - a.gaps || compareSortText(a.name, b.name),
    );
  }, [searched, filter, stores, storeId, searching, staff]);

  // Totalled from the SEARCHED rows, not the gap-filtered ones: "out of stock only"
  // narrows to products that are out somewhere, but still shows their full network
  // figures instead of re-totalling them over the empty shelves.
  const productList = useMemo<ProductLine[]>(() => {
    const list = productLines(searched);
    return filter === 'out' ? list.filter((l) => l.gaps > 0) : list;
  }, [searched, filter]);
  const productHeading = useMemo(() => sizesHeading(searched), [searched]);

  // A→Z until a header is clicked.
  const productSort = useTableSort<ProductLine>(productList, {
    label: (t) => t.name,
    stores: (t) => t.stores,
    available: (t) => t.available,
    sold: (t) => t.sold,
  });

  const expandableIds = storeGroups.filter((g) => g.rows.length > 0).map((g) => g.id);
  const allOpen = expandableIds.length > 0 && expandableIds.every((id) => expanded.has(id));

  const toggleGroup = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const switchView = (next: View) => {
    setView(next);
    setExpanded(new Set());
    // Product view is network-wide by definition and hides the store filter, so a
    // store left selected would silently scope it to one shop.
    if (next === 'product') {
      setStoreId('');
      // "Not updated" is about stores; it has no meaning in a list of products.
      if (filter === 'stale') setFilter('all');
    }
  };

  const slug = phrases.lower.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const nothingToExport = view === 'store'
    ? storeGroups.every((g) => g.rows.length === 0)
    : productList.length === 0;

  // Product view downloads what is on screen, in the order shown. Store view
  // downloads every figure behind each opened store (last count, added, removed,
  // sources): the screen keeps to available and sold, the file keeps the detail.
  const handleDownload = () => {
    const csv = view === 'product'
      ? buildCsv(
          ['Product', productHeading, 'Stores', 'Available (all stores)', 'Sold (all stores)'],
          productSort.sorted.map((t) => [
            t.name,
            t.sizes.map((z) => `${z.label}: ${z.available}`).join(', '),
            String(t.stores),
            String(t.available),
            String(t.sold),
          ]),
        )
      : buildCsv(
          ['Store', 'Product', 'Level', 'Status', 'Last count', 'Counted on', 'Added', 'Sold', 'Removed', 'On hand', 'Last movement', 'Last updated by', 'Sources'],
          storeGroups.flatMap((g) => g.rows).map((r) => [
            r.storeName ?? '',
            r.label,
            r.isVersion ? 'Version' : 'Whole product',
            statusRank(r) === 0 ? phrases.outOf : statusRank(r) === 1 ? 'Never counted' : 'Available',
            r.opening === null ? '' : String(r.opening),
            csvDate(r.openingAt),
            String(r.added),
            String(r.reduced),
            String(r.removed),
            String(r.available),
            csvDate(r.lastMovementAt),
            who(r.lastByName, r.lastByCode) ?? '',
            sourcesText(r, '; '),
          ]),
        );
    downloadBlob(
      `running-${slug}-by-${view}.csv`,
      new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
    );
  };

  const VIEWS: { value: View; label: string; icon: typeof StoreIcon }[] = [
    { value: 'store', label: 'Store view', icon: StoreIcon },
    { value: 'product', label: 'Product view', icon: Package },
  ];

  const noMatchMessage = searching
    ? `Nothing matches “${search.trim()}”.`
    : filter === 'stale'
      ? `Every reporting store was updated in the last ${STALE_DAYS} days.`
      : `Nothing is ${phrases.outOfLower}.`;

  return (
    <ReportPageScaffold
      title={`Running ${phrases.noun}`}
      searchValue={search}
      onSearchChange={(e) => setSearch(e.target.value)}
      searchPlaceholder={view === 'store' ? 'Search store or product…' : 'Search product or version…'}
      tabs={
        <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
          {VIEWS.map((v) => {
            const Icon = v.icon;
            return (
              <button
                key={v.value}
                type="button"
                onClick={() => switchView(v.value)}
                className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                  view === v.value ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                {v.label}
              </button>
            );
          })}
        </div>
      }
      filters={
        <>
          {view === 'store' && (
            <select
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">All stores</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={handleDownload}
            disabled={loading || nothingToExport}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Download
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowSettings((v) => !v)}
            disabled={enabled === null}
            title="What this project calls the thing it counts, and whether promoters see it"
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium disabled:opacity-50 ${
              showSettings
                ? 'border-primary-300 bg-primary-50 text-primary-700'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <SettingsIcon className="h-4 w-4" /> Settings
          </button>
        </>
      }
    >
      {error && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
      )}

      {/* The one setting worth interrupting for — and only while it is wrong. A
          muted caption rather than a banner: the report is still correct, the
          promoters just cannot see it. */}
      {enabled === false && (
        <p className="mt-4 flex flex-wrap items-center gap-1.5 text-xs text-amber-700">
          <AlertTriangle className="h-3.5 w-3.5" />
          Promoters can’t see these figures in the app — “{phrases.showToPromoters}” is off.
          <button
            type="button"
            onClick={() => toggleTracking(true)}
            disabled={savingFlag}
            className="font-semibold underline underline-offset-2 hover:text-amber-800 disabled:opacity-50"
          >
            Turn it on
          </button>
        </p>
      )}

      {showSettings && enabled !== null && (
        <div className="mt-4 flex flex-wrap items-start justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50/60 p-3">
          <div className="min-w-[240px] flex-1">
            <label htmlFor="balance-label" className="text-xs font-medium text-gray-900">
              What do you call the thing you count?
            </label>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <input
                id="balance-label"
                value={labelDraft}
                maxLength={40}
                disabled={savingLabel}
                onChange={(e) => setLabelDraft(e.target.value)}
                onBlur={saveLabel}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                placeholder={DEFAULT_BALANCE_LABEL}
                className="w-48 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm disabled:opacity-50"
              />
              <span className="text-xs text-gray-500">
                Reads as “{phrases.effectSet}”. Empty = “{DEFAULT_BALANCE_LABEL}”.
              </span>
            </div>
          </div>
          <div className="flex max-w-sm items-start gap-3">
            <label className="relative mt-0.5 inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                checked={enabled}
                disabled={savingFlag}
                onChange={(e) => toggleTracking(e.target.checked)}
                className="peer sr-only"
              />
              <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-2 peer-focus:ring-primary-300"></div>
            </label>
            <div className="text-xs text-gray-600">
              <span className="font-medium text-gray-900">{phrases.showToPromoters}</span>
              <p className="mt-0.5">
                Shows “Available: N” in the mobile app. Off hides it from promoters —
                figures are still recorded, so turning it on later reveals the history.
              </p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <Skeleton className="mt-4 h-64 w-full" />
      ) : rows.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center">
          <p className="text-sm text-gray-600">{phrases.nothingRecorded}</p>
          {/* The empty state has to teach: it is what an admin hits first, and
              the cause is almost always the same missing step. */}
          <p className="mx-auto mt-2 max-w-lg text-xs text-gray-500">
            A figure appears here once a task question says what its number does. Open the task,
            edit a Number question, and set <span className="font-medium">{phrases.trackWithThis}</span>{' '}
            to “{phrases.effectSet}” for a count, or “{phrases.effectDecrease}” for a sale.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile
              icon={StoreIcon}
              label="Stores reporting"
              value={`${summary.reporting} / ${summary.totalStores}`}
              hint={summary.silent > 0 ? `${plural(summary.silent, 'store')} with nothing recorded` : 'Every store has figures'}
            />
            <Tile
              icon={Package}
              label="Products tracked"
              value={summary.products}
              hint={summary.versions > summary.products ? `${summary.versions} sizes with at least one figure` : 'with at least one figure recorded'}
            />
            {/* Each of the next two tiles IS a filter — the number that
                matters, and one click to see exactly those stores. */}
            <Tile
              icon={AlertTriangle}
              label={phrases.outOf}
              value={summary.gaps}
              tone={summary.gaps > 0 ? 'text-red-600' : 'text-gray-900'}
              hint={
                filter === 'out'
                  ? 'Showing only these · click to clear'
                  : summary.gaps === 0
                    ? 'Nothing counted is at zero'
                    : `sizes counted and now empty · at ${plural(summary.gapStores, 'store')}`
              }
              onClick={summary.gaps > 0 || filter === 'out' ? () => toggleFilter('out') : undefined}
              active={filter === 'out'}
            />
            <Tile
              icon={Clock}
              label={`Not updated in ${STALE_DAYS}+ days`}
              value={plural(summary.staleStores, 'store')}
              tone={summary.staleStores > 0 ? 'text-amber-600' : 'text-gray-900'}
              hint={
                filter === 'stale'
                  ? 'Showing only these · click to clear'
                  : `latest figure anywhere: ${fmtDate(summary.lastAt)}`
              }
              onClick={
                view === 'store' && (summary.staleStores > 0 || filter === 'stale')
                  ? () => toggleFilter('stale')
                  : undefined
              }
              active={filter === 'stale'}
            />
          </div>

          {view === 'store' ? (
            storeGroups.length === 0 ? (
              <p className="mt-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                {noMatchMessage}
              </p>
            ) : (
              <div className="mt-4">
                {!searching && expandableIds.length > 1 && (
                  <div className="mb-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setExpanded(allOpen ? new Set() : new Set(expandableIds))}
                      className="text-xs font-medium text-primary-600 hover:text-primary-700"
                    >
                      {allOpen ? 'Collapse all' : 'Expand all'}
                    </button>
                  </div>
                )}
                <div className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200">
                  {storeGroups.map((g) => {
                    // Searching or filtering opens every match: the reader is
                    // hunting, and making them click into each shop defeats it.
                    const open = searching || expanded.has(g.id);
                    const empty = g.rows.length === 0;
                    return (
                      <div key={g.id}>
                        <button
                          type="button"
                          onClick={() => toggleGroup(g.id)}
                          disabled={empty}
                          className={`flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors ${
                            empty ? 'cursor-default bg-white' : 'bg-white hover:bg-gray-50'
                          }`}
                        >
                          {empty ? (
                            <span className="h-4 w-4 flex-shrink-0" />
                          ) : open ? (
                            <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-gray-900">{g.name}</span>
                            {/* Who is there, and who was last heard from — the
                                two things a figure alone never says. */}
                            <span className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
                              <span
                                className="inline-flex items-center gap-1"
                                title={g.staff.map((p) => `${who(p.name, p.employeeCode)}${p.designation ? ` · ${p.designation}` : ''}`).join('\n')}
                              >
                                <Users className="h-3 w-3 text-gray-400" />
                                {g.staff.length === 0 ? (
                                  <span className="text-gray-400">No one deployed at this store</span>
                                ) : (
                                  <>
                                    {g.staff.slice(0, 3).map((p) => who(p.name, p.employeeCode)).join(', ')}
                                    {g.staff.length > 3 && ` +${g.staff.length - 3}`}
                                  </>
                                )}
                              </span>
                              {!empty && (
                                <span className={g.stale ? 'text-amber-600' : 'text-gray-400'}>
                                  Last update {fmtDate(g.lastMovementAt)}{g.lastBy ? ` by ${g.lastBy}` : ''}
                                </span>
                              )}
                            </span>
                          </span>
                          {empty ? (
                            <span className="text-xs text-gray-400">Nothing recorded yet</span>
                          ) : (
                            // Enough on the collapsed row that most shops never
                            // need opening: how much is there, how much went, and
                            // what has run out.
                            <span className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-xs">
                              <span className="font-semibold tabular-nums text-gray-900">{g.onHand} available</span>
                              {g.sold > 0 && <span className="tabular-nums text-gray-500">{g.sold} sold</span>}
                              {g.gaps > 0 && (
                                <span className="rounded-full bg-red-50 px-2 py-0.5 font-medium text-red-700">
                                  {g.gaps} {phrases.outOfLower}
                                </span>
                              )}
                            </span>
                          )}
                        </button>

                        {open && !empty && <StorePanel group={g} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          ) : productList.length === 0 ? (
            <p className="mt-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
              {noMatchMessage}
            </p>
          ) : (
            // The same lines as an opened store, added up over every store: how much
            // of each product is out there, by size, and how much has gone.
            <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-gray-50/70">
                  <tr className="text-left text-xs text-gray-500">
                    <SortTh label="Product" sortKey="label" sort={productSort} className="px-4 py-2 font-medium" />
                    <th className="px-3 py-2 font-medium">{productHeading}</th>
                    <SortTh
                      label="Stores"
                      sortKey="stores"
                      sort={productSort}
                      align="right"
                      className="px-3 py-2 font-medium"
                      title="Stores with a figure for it"
                    />
                    <SortTh
                      label="Available"
                      sortKey="available"
                      sort={productSort}
                      align="right"
                      className="px-3 py-2 font-medium"
                      title={`${AVAILABLE_HINT}, added up over every store`}
                    />
                    <SortTh
                      label="Sold"
                      sortKey="sold"
                      sort={productSort}
                      align="right"
                      className="px-4 py-2 font-medium"
                      title={SOLD_HINT}
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {productSort.sorted.map((t) => (
                    <tr key={t.key} className="hover:bg-gray-50/60">
                      <td className="px-4 py-2.5 font-medium text-gray-900">{t.name}</td>
                      <td className="px-3 py-2"><SizeChips sizes={t.sizes} /></td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{t.stores}</td>
                      <td className="px-3 py-2.5 text-right text-base font-semibold tabular-nums text-gray-900">
                        <AvailableFigure line={t} />
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">
                        {t.sold || <span className="text-gray-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="mt-3 flex items-start gap-1.5 text-[11px] text-gray-400">
            <Info className="mt-0.5 h-3 w-3 flex-shrink-0" />
            {view === 'store'
              ? 'Available = the last count, plus what was added, minus what was sold or removed since that count. Sold counts sales since the last count; transfers and returns are not sales. A red size has more sold or removed than was counted: count it again. Download has every figure.'
              : 'Available and sold are added up over every store. A product nobody has counted or sold yet is not listed.'}
          </p>
        </>
      )}
    </ReportPageScaffold>
  );
}
